import React, { useState } from "react";
import {
  Datagrid,
  Filter,
  List,
  SearchInput,
  ReferenceField,
  ListProps,
  FunctionField,
  TextField,
  useDataProvider,
  useNotify,
  useRedirect,
} from "react-admin";
import { Helmet } from "react-helmet";
import { Grid, Typography, Button, CircularProgress } from "@material-ui/core";
import SanitizedGrid from "components/SanitizedGrid";
import { GridinRow, GridLabel } from "utils/style";
import CustomDateField from "components/CustomDateField";
import StatusField from "components/StatusField";
import moment from "moment";
import TypeField from "components/TypeField";
import EditIcon from "@material-ui/icons/Edit";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { SchoolIdSelected } from "utils/tools";

const RoutePlanFilter = (props: any) => (
  <Filter {...props}>
    <SearchInput source="search" alwaysOn />
  </Filter>
);

const RoutePlanList = (props: ListProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const provider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();

  const RoutePlanEditButton = (record: IBusSchedulePlans) => {
    const handleDuplicateRoutePlan = async () => {
      setLoading(true);
      try {
        await provider.customUpdate(`busSchedulePlans/${record.id}/duplicate`, {
          data: {},
        });
        setLoading(false);

        redirect(`/busSchedulePlans/${record.id}/`);
      } catch (error: any) {
        setLoading(false);
        notify(
          `Failed duplicate the route plan, error: ${error.message}`,
          "warning"
        );
      }
    };

    return (
      <Button onClick={handleDuplicateRoutePlan}>
        {loading ? <CircularProgress size={30} /> : <EditIcon />}
      </Button>
    );
  };

  const RoutePlanShowButton = (record: IBusSchedulePlans) => {
    return (
      <Button href={`/#/busSchedulePlans/${record.id}/show`}>
        <VisibilityIcon />
      </Button>
    );
  };

  return (
    <>
      <Helmet>
        <title>Route Plans</title>
      </Helmet>
      <List
        exporter={false}
        bulkActionButtons={false}
        filters={<RoutePlanFilter />}
        filterDefaultValues={{
          SchoolId: Number(SchoolIdSelected()),
        }}
        title="Route Plans"
        {...props}
      >
        <Datagrid>
          <ReferenceField
            label="School"
            source="busScheduleId"
            reference="busSchedules"
            emptyText="-"
            link={false}
          >
            <ReferenceField source="schoolId" reference="schools" link={false}>
              <TextField source="name" />
            </ReferenceField>
          </ReferenceField>

          <ReferenceField
            label="Wave"
            source="busScheduleId"
            reference="busSchedules"
            emptyText="-"
            link={false}
            sortBy="waveName"
          >
            <TextField source="name" />
          </ReferenceField>
          <FunctionField
            label="Route Plan Created"
            render={() => (
              <SanitizedGrid container spacing={2}>
                <Grid item xs={12} style={GridinRow}>
                  <Typography variant="body2" style={GridLabel}>
                    Date :
                  </Typography>
                  <CustomDateField source="createdAt" isDateTime />
                </Grid>

                <Grid item xs={12} style={GridinRow}>
                  <Typography variant="body2" style={GridLabel}>
                    Days :
                  </Typography>
                  <ReferenceField
                    source="busScheduleId"
                    reference="busSchedules"
                    emptyText="-"
                    link={false}
                  >
                    <FunctionField
                      render={(record: any) => {
                        let days: string = "";

                        if (record.isMonday) {
                          days = days + " Monday,";
                        }
                        if (record.isTuesday) {
                          days = days + " Tuesday,";
                        }
                        if (record.isWednesday) {
                          days = days + " Wednesday,";
                        }
                        if (record.isThursday) {
                          days = days + " Thursday,";
                        }
                        if (record.isFriday) {
                          days = days + " Friday,";
                        }

                        return days.replace(/,(?=[^,]*$)/, "");
                      }}
                    />
                  </ReferenceField>
                </Grid>
              </SanitizedGrid>
            )}
          />
          <FunctionField
            label="Route Plan Revised"
            render={(record: any) => {
              const createdAt = moment.utc(record.createdAt);
              const updateAt = moment.utc(record.updatedAt);
              const diffDate = updateAt.diff(createdAt, "seconds");

              if (diffDate < 1) {
                return "-";
              } else {
                return updateAt.format("DD / MM / YYYY, hh:mm A");
              }
            }}
          />
          <CustomDateField
            label="Next Effective Date"
            source="nextEffectiveDate"
          />
          <ReferenceField
            label="Type"
            source="busScheduleId"
            reference="busSchedules"
            emptyText="-"
            link={false}
          >
            <TypeField source="type" type="busSchedule" />
          </ReferenceField>
          <StatusField source="status" />
          <FunctionField
            label="Actions"
            render={(props: any) => (
              <>
                {props.routePlan !== null && <RoutePlanShowButton {...props} />}

                <RoutePlanEditButton {...props} />
              </>
            )}
          />
        </Datagrid>
      </List>
    </>
  );
};

export default RoutePlanList;
